import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { CARO_CLAUSES, Question } from "./caro-clauses-data";

interface CAROClausePageProps {
    clauseId: string;
    setActiveSection: (section: string) => void;
}

export default function CAROClausePage({ clauseId, setActiveSection }: CAROClausePageProps) {
    const { isArchivalMode } = useArchivalMode();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [responses, setResponses] = useState<Record<string, string>>({});
    const [clauseConfig, setClauseConfig] = useState(CARO_CLAUSES[clauseId]);

    useEffect(() => {
        setClauseConfig(CARO_CLAUSES[clauseId]);
        loadData();
    }, [clauseId]);

    const mapJSONToResponses = (questions: Record<string, string>): Record<string, string> => {
        const mapped: Record<string, string> = {};
        Object.keys(questions).forEach((qKey) => {
            // Q1 -> q1
            const qNum = qKey.replace('Q', '');
            const value = questions[qKey];
            if (value === "Yes") mapped[`q${qNum}`] = "yes";
            else if (value === "No") mapped[`q${qNum}`] = "no";
            else mapped[`q${qNum}`] = "";
        });
        return mapped;
    };

    const mapResponsesToJSON = (responses: Record<string, string>): Record<string, string> => {
        const mapped: Record<string, string> = {};
        const sortedKeys = Object.keys(responses).sort((a, b) => {
            const numA = parseInt(a.replace('q', ''), 10);
            const numB = parseInt(b.replace('q', ''), 10);
            return numA - numB;
        });

        sortedKeys.forEach((key) => {
            const questionNum = key.replace('q', '');
            const value = responses[key];
            if (value === "yes") mapped[`Q${questionNum}`] = "Yes";
            else if (value === "no") mapped[`Q${questionNum}`] = "No";
            else mapped[`Q${questionNum}`] = "";
        });
        return mapped;
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await (window as any).sharePointAPI?.downloadCaroJson();
            if (result?.success && result.data?.CARO_Clauses) {
                const clauseData = result.data.CARO_Clauses.find((c: any) => c.clause === clauseId);
                if (clauseData && clauseData.questions) {
                    setResponses(mapJSONToResponses(clauseData.questions));
                }
            }
        } catch (error) {
            console.error("Error loading CARO data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            // Download fresh data first (optimistic locking)
            const downloadResult = await (window as any).sharePointAPI?.downloadCaroJson();
            if (!downloadResult?.success) {
                throw new Error("Failed to download latest data");
            }

            const caroData = downloadResult.data;
            const clauseIndex = caroData.CARO_Clauses.findIndex((c: any) => c.clause === clauseId);

            if (clauseIndex === -1) {
                throw new Error("Clause not found in data");
            }

            // Map current responses
            const mappedQuestions = mapResponsesToJSON(responses);
            const existingQuestions = caroData.CARO_Clauses[clauseIndex].questions || {};
            const updatedQuestions: Record<string, string> = {};

            // Merge logic
            const allKeys = new Set([...Object.keys(existingQuestions), ...Object.keys(mappedQuestions)]);
            allKeys.forEach(key => {
                if (mappedQuestions[key] !== undefined) updatedQuestions[key] = mappedQuestions[key];
                else updatedQuestions[key] = existingQuestions[key];
            });

            caroData.CARO_Clauses[clauseIndex].questions = updatedQuestions;

            // Upload
            const uploadResult = await (window as any).sharePointAPI?.uploadCaroJson(caroData);
            if (!uploadResult?.success) {
                throw new Error("Failed to upload data");
            }

            // Navigate back
            setActiveSection("concluding-caro");
        } catch (error) {
            console.error("Error saving responses:", error);
            alert("Failed to save responses");
        } finally {
            setSaving(false);
        }
    };

    const shouldShowQuestion = (question: Question): boolean => {
        if (!question.dependsOn) return true;
        const dependentResponse = responses[question.dependsOn];
        return dependentResponse === question.showIf;
    };

    if (!clauseConfig) return <div>Clause not found</div>;

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-white cursor-pointer transition-colors" onClick={() => setActiveSection("concluding-caro")}>
                        <ArrowLeft className="h-4 w-4" />
                        <span>Back to CARO Landing</span>
                    </div>
                    <h1 className="text-2xl font-bold text-white">{clauseConfig.title}</h1>
                </div>
                <div className="flex gap-2">
                    {/* Add Review Comment Button later if needed */}
                </div>
            </div>

            <Card className="glass-card border-white/10">
                <CardContent className="p-6">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {clauseConfig.questions.map((question) => {
                                if (!shouldShowQuestion(question)) return null;
                                return (
                                    <div key={question.id} className="space-y-3 p-4 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                        <p className="text-base text-white/90">{question.text}</p>
                                        <div className="flex gap-6">
                                            <label className="flex items-center gap-2 cursor-pointer group">
                                                <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${responses[question.id] === "yes" ? "border-blue-400 bg-blue-400/20" : "border-white/20 group-hover:border-white/40"}`}>
                                                    {responses[question.id] === "yes" && <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />}
                                                </div>
                                                <input
                                                    type="radio"
                                                    name={question.id}
                                                    value="yes"
                                                    checked={responses[question.id] === "yes"}
                                                    onChange={(e) => setResponses(p => ({ ...p, [question.id]: e.target.value }))}
                                                    className="hidden"
                                                />
                                                <span className={`text-sm ${responses[question.id] === "yes" ? "text-white" : "text-white/60 group-hover:text-white/80"}`}>Yes</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer group">
                                                <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${responses[question.id] === "no" ? "border-blue-400 bg-blue-400/20" : "border-white/20 group-hover:border-white/40"}`}>
                                                    {responses[question.id] === "no" && <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />}
                                                </div>
                                                <input
                                                    type="radio"
                                                    name={question.id}
                                                    value="no"
                                                    checked={responses[question.id] === "no"}
                                                    onChange={(e) => setResponses(p => ({ ...p, [question.id]: e.target.value }))}
                                                    className="hidden"
                                                />
                                                <span className={`text-sm ${responses[question.id] === "no" ? "text-white" : "text-white/60 group-hover:text-white/80"}`}>No</span>
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setActiveSection("concluding-caro")} className="text-white/60 hover:text-white">Cancel</Button>
                <Button onClick={handleSave} disabled={saving || isArchivalMode || loading} className="bg-blue-500 hover:bg-blue-600">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Responses
                </Button>
            </div>
        </div>
    );
}
